import { useState } from "react";

import { Platform, Keyboard } from "react-native";
import Config from "react-native-config";
import Constants, {
  NavigationScreenName,
  RegisterUseCase,
} from "@lib/utilities/Constants";
import Storage from "@lib/utilities/storage/StorageFunctions";
import { CommonActions, useNavigation } from "@react-navigation/native";
import { Logger } from "@lib/utilities/HelperFunctions";
import {
  inputPhoneFormatter,
  removeExtraSpace,
} from "@lib/utilities/Formatters";
import { createUser, getUserIdFromEmail } from "@lib";
import { grantAppAccess, includeUserFromClientGroup } from "@lib";
import {
  isValidEmail,
  isValidPassword,
  isValidPhone,
} from "@lib/utilities/Validators";
import { useAuth0 } from "@auth0/auth0-react";
import { InitialFormFieldsType } from "./type";
import { messages } from "../../../src/resources/strings";
import { useAppSelector } from "@lib";

export default function RegisterViewModel() {
  const [errorHandle, setErrorHandle] = useState(messages.error.noError);
  const [isLoading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [firstNameHelpText, setFirstNameHelpText] = useState("");
  const [lastNameHelpText, setLastNameHelpText] = useState("");
  const [emailHelpText, setEmailHelpText] = useState(
    messages.validation.emailHint
  );
  const [passwordHelpText, setPasswordHelpText] = useState("");
  const [confirmPasswordHelpText, setConfirmPasswordHelpText] = useState("");
  const [phoneHelpText, setPhoneHelpText] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isInitialStep, setInitialStep] = useState(true);
  const [isInitialSubmit, setInitialSubmit] = useState(false);
  const [isFinalSubmit, setFinalSubmit] = useState(false);
  const [isRegistered, setRegistered] = useState(false);
  const [headerLabel, setHeaderLabel] = useState("Create your account");

  const [showPasswordLabel, setShowPasswordLabel] = useState("Show");
  const [showConfirmPasswordLabel, setShowConfirmPasswordLabel] =
    useState("Show");
  const [isVerifyEmail, setVerifyEmail] = useState(true);
  const keyboardVerticalOffset = 0;
  const { isConnected } = useAppSelector((state) => state.userReducer);

  const { getAccessTokenSilently } = useAuth0();

  const navigation = useNavigation();

  const checkSession = async () => {
    const tokenLS = await Storage.getData(
      Constants.LocalStoreConstant.ACCESS_TOKEN
    );
    if (typeof tokenLS !== "undefined") {
      if (Platform.OS == "web") {
        Constants.User.ACCESS_TOKEN = tokenLS;
        const isReloading = await Storage.getData(
          Constants.LocalStoreConstant.IS_RELOADING
        );
        if (!isReloading) {
          navigation.navigate(NavigationScreenName.choosePolicy as never);
        } else {
          Storage.storeData(Constants.LocalStoreConstant.IS_RELOADING, false);
        }
      } else {
        Constants.User.ACCESS_TOKEN = tokenLS;
        navigation.dispatch(
          CommonActions.reset({
            index: 1,
            routes: [{ name: "ChoosePolicy" }],
          })
        );
      }
    }
  };

  //API ::
  const checkEmailForRegistration = async (value: InitialFormFieldsType) => {
    const { email } = value;
    if (!validateEmail(email, true)) {
      return { useCase: RegisterUseCase.none, userId: "0" };
    }
    const object = await getUserIdFromEmail(value);

    if (object.errorCode == messages.error.noError) {
      setEmailHelpText("");
      setErrorHandle(messages.error.noError);
      if (object.response.message === messages.error.invalidData) {
        Logger("Email exists in pool and has PH access");
        setVerifyEmail(true);
        setEmailHelpText(messages.validation.emailAlreadyExists);
        return {
          useCase: RegisterUseCase.existingUser,
          userId: object.response.sub,
        };
      } else if (object.response.message === messages.error.validData) {
        Logger("New email - Go Register");
        setVerifyEmail(true);
        setEmailHelpText("");
        return { useCase: RegisterUseCase.newUser, userId: "0" };
      } else {
        Logger("Email exists in pool and has not PH access");
        setVerifyEmail(false);
        setEmailHelpText("");
        return {
          useCase: RegisterUseCase.existingUserWithNoAccess,
          userId: object.response.sub,
        };
      }
    } else if (object.errorCode === messages.error.noUserFound) {
      // Deprecated: Need to remove
      Logger("New email - Go Register");
      setVerifyEmail(true);
      setEmailHelpText("");
      return { useCase: RegisterUseCase.newUser, userId: "0" };
    } else {
      return { useCase: RegisterUseCase.none, userId: "0" };
    }
  };

  const handleCheckEmailForRegistrationResponse = async (
    responseObject: any
  ) => {
    if (responseObject.useCase == RegisterUseCase.newUser) {
      Logger("New user");
      setLoading(false);
      setInitialStep(false);
      setHeaderLabel("Set a password");
    } else if (
      responseObject.useCase == RegisterUseCase.existingUserWithNoAccess
    ) {
      if (responseObject.userId != null) {
        const clientId =
          Platform.OS == "web"
            ? process.env.REACT_APP_EXZEO_ID_CLIENT_ID
            : Config.REACT_APP_EXZEO_ID_CLIENT_ID;
        await includeUserFromClientGroup(responseObject.userId, clientId ?? "");
        const object = await grantAppAccess(
          responseObject.userId,
          clientId ?? ""
        );
        if (object.errorCode == messages.error.noError) {
          setErrorHandle(messages.error.noError);
          Logger("Access granted");
          setRegistered(true);
          await grantAccessToHarmony(responseObject.userId);
        } else {
          setErrorHandle(messages.error.errorInGrantingAccess);
          Logger("Error in granting access");
        }
        setLoading(false);
      } else {
        Logger("User id required for granting access");
        setLoading(false);
      }
    } else if (responseObject.useCase == RegisterUseCase.existingUser) {
      Logger("User already registered and access granted. Navigate to login");
      setLoading(false);
    } else {
      Logger("Something went wrong");
      setErrorHandle(messages.error.somthingWrong);
      setLoading(false);
    }
  };

  const validateFirstName = (value: string, showHelpText: boolean) => {
    Logger("validateFirstName ======");
    setFirstName(value);
    setFirstNameHelpText("");
    if (value.trim() === "") {
      showHelpText &&
        setFirstNameHelpText(messages.validation.firstNameRequired);
      return false;
    } else {
      return true;
    }
  };

  const validateLastName = (value: string, showHelpText: boolean) => {
    setLastName(value);
    setLastNameHelpText("");
    if (value.trim() === "") {
      showHelpText && setLastNameHelpText(messages.validation.lastNameRequired);
      return false;
    } else {
      return true;
    }
  };

  const validateEmail = (value: string, showHelpText: boolean) => {
    const trimmedValue = removeExtraSpace(value);
    setEmail(trimmedValue);
    setEmailHelpText(messages.validation.emailHint);
    if (trimmedValue === "") {
      showHelpText && setEmailHelpText(messages.validation.emailRequired);
      return false;
    } else if (!isValidEmail(trimmedValue)) {
      showHelpText && setEmailHelpText(messages.validation.wrongEmailFormat);
      return false;
    } else {
      return true;
    }
  };

  const validatePhone = (value: string, showHelpText: boolean) => {
    const formattedNumber = inputPhoneFormatter(value, phone);
    setPhone(formattedNumber);
    setPhoneHelpText("");
    if (formattedNumber === "") {
      showHelpText && setPhoneHelpText(messages.validation.phoneRequired);
      return false;
    } else if (!isValidPhone(formattedNumber)) {
      showHelpText && setPhoneHelpText(messages.validation.invalidNumber);
      return false;
    } else {
      return true;
    }
  };

  const validatePassword = (value: string, showHelpText: boolean) => {
    setPassword(value);
    setPasswordHelpText("");
    if (value === "") {
      showHelpText && setPasswordHelpText(messages.validation.passcodeRequired);
      return false;
    } else if (!isValidPassword(value)) {
      showHelpText &&
        setPasswordHelpText(messages.validation.invalidPasscodeFormat);
      return false;
    } else {
      return true;
    }
  };

  const matchPasswords = (password1: string, password2: string) => {
    setConfirmPassword(password2);
    const isPasswordValid = validatePassword(password1, true);
    if (!isPasswordValid) {
      return false;
    }
    if (password2 === "") {
      setConfirmPasswordHelpText(messages.validation.passcodeRequired);
      return false;
    } else if (password1 === password2) {
      setConfirmPasswordHelpText("");
      return true;
    }
    setConfirmPasswordHelpText(messages.validation.codeMismatch);
    return false;
  };

  const togglePasswordVisiblity = (value: boolean) => {
    setShowPassword(value);
    if (value) {
      setShowPasswordLabel("Hide");
    } else {
      setShowPasswordLabel("Show");
    }
  };
  const toggleConfirmPasswordVisiblity = (value: boolean) => {
    setShowConfirmPassword(value);
    if (value) {
      setShowConfirmPasswordLabel("Hide");
    } else {
      setShowConfirmPasswordLabel("Show");
    }
  };

  const validateValuesOnSubmit = () => {
    const isFirstNameValid = validateFirstName(firstName, true);
    const isLastNameValid = validateLastName(lastName, true);
    const isEmailValid = validateEmail(email, true);
    const isPhoneValid = validatePhone(phone, true);
    return isFirstNameValid && isLastNameValid && isEmailValid && isPhoneValid;
  };

  const grantAccessToHarmony = async (_userId: string) => {
    const clientId =
      Platform.OS == "web"
        ? process.env.REACT_APP_HARMONY_API_CLIENT_ID
        : Config.REACT_APP_HARMONY_API_CLIENT_ID;
    const responseObj = await grantAppAccess(_userId, clientId ?? "");
    setErrorHandle(messages.error.noError);
    if (responseObj.errorCode == messages.error.noError) {
      Logger("Access granted to harmony");
    } else {
      Logger("Access grant failed to harmony");
    }
  };

  const registerNewUser = async () => {
    Keyboard.dismiss();
    if (!isConnected) {
      Platform.OS == "web"
        ? setErrorHandle(messages.error.youAreOffline)
        : showNetworkError();
      return;
    }
    setErrorHandle(messages.error.noError);
    setFinalSubmit(true);
    if (!matchPasswords(password, confirmPassword)) {
      return;
    }
    Logger("Post /user");
    const object = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: removeExtraSpace(email),
      phone: phone,
      password: password,
    };
    setLoading(true);
    const responseObj = await createUser(object);
    if (responseObj.errorCode == messages.error.noError) {
      Logger("Registered Successfully");
      setErrorHandle(messages.error.noError);
      setRegistered(true);
      // await grantAccessToHarmony(responseObj.response.global_id); removed grant API call after new user register success
    } else {
      setErrorHandle(messages.error.registrationFailed);
      Logger("Registration failed" + responseObj);
    }
    setLoading(false);
  };

  const submitInitialForm = async () => {
    Keyboard.dismiss();
    if (!isConnected) {
      Platform.OS == "web"
        ? setErrorHandle(messages.error.youAreOffline)
        : showNetworkError();
      return;
    }
    setErrorHandle(messages.error.noError);
    setInitialSubmit(true);
    await Storage.clearData(Constants.LocalStoreConstant.HAS_HARMONY_ACCESS);
    const isvalidated = validateValuesOnSubmit();
    if (!isvalidated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const response = await checkEmailForRegistration({
      firstName,
      lastName,
      phone,
      email,
    });
    handleCheckEmailForRegistrationResponse(response);
  };

  const getAuthAccessToken = async () => {
    let accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_EXZEO_ID_AUDIENCE,
        scope: "openid email profile",
      },
    });
    Constants.User.ACCESS_TOKEN = `Bearer ${accessToken}`;
    await Storage.storeData(
      Constants.LocalStoreConstant.ACCESS_TOKEN,
      Constants.User.ACCESS_TOKEN
    );
    Logger("Login successful !" + Constants.User.ACCESS_TOKEN);
    navigation.dispatch(
      CommonActions.reset({
        index: 1,
        routes: [{ name: "ChoosePolicy" }],
      })
    );
  };

  const showNetworkError = async () => {
    return;
  };

  let listener = () => {
    getAuthAccessToken();
  };

  return {
    errorHandle,
    isLoading,
    firstName,
    lastName,
    email,
    password,
    confirmPassword,
    phone,
    firstNameHelpText,
    lastNameHelpText,
    emailHelpText,
    passwordHelpText,
    confirmPasswordHelpText,
    phoneHelpText,
    showPassword,
    isInitialStep,
    isInitialSubmit,
    isFinalSubmit,
    isRegistered,
    headerLabel,

    showPasswordLabel,
    showConfirmPasswordLabel,
    showConfirmPassword,
    isVerifyEmail,
    keyboardVerticalOffset,
    setConfirmPassword,
    setErrorHandle,
    checkSession,
    checkEmailForRegistration,
    validateFirstName,
    validateLastName,
    validateEmail,
    validatePhone,
    validatePassword,
    matchPasswords,
    togglePasswordVisiblity,
    toggleConfirmPasswordVisiblity,
    validateValuesOnSubmit,
    grantAccessToHarmony,

    registerNewUser,
    submitInitialForm,
    getAuthAccessToken,
    listener,
  };
}
